import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useEffect, useState } from "react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../firebase/config";
import { offlineAuthService } from "../../services/offlineAuthService";
import { employeeCanAccessPath, firstAllowedPath, isActivePanelEmployee, PanelAccess } from "../../auth/adminAccess";
import peakhero from '../../assets/peakhero.jpg';
import googleIcon from '../../assets/google-icon.webp';

const LoginPage: React.FC = () => {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const { login, loginWithGoogle, logout, authState, currentUser } = useAuth();
    const [localError, setLocalError] = useState<string | null>(null);
    const navigate = useNavigate();
    const location = useLocation();

    const isOffline = !navigator.onLine;
    const hasCachedAuth = offlineAuthService.hasCachedAuth();

    // Handle navigation after successful login - verify user is an admin
    useEffect(() => {
        if (!currentUser || authState.loading) return;

        const checkEmployeeAndNavigate = async () => {
            try {
                const employeeDocRef = doc(db, 'employees', currentUser.uid);
                const employeeDoc = await getDoc(employeeDocRef);

                const data = employeeDoc.exists() ? employeeDoc.data() : undefined;
                const permissions: unknown = data?.permissions;
                const access: PanelAccess = {
                    status: typeof data?.status === 'string' ? data.status : undefined,
                    enabled: typeof data?.enabled === 'boolean' ? data.enabled : undefined,
                    permissions: Array.isArray(permissions) ? permissions.filter((p): p is string => typeof p === 'string') : [],
                    superAdmin: data?.superAdmin === true,
                };

                if (isActivePanelEmployee(access)) {
                    const requested = (location.state as { from?: string } | null)?.from;
                    const destination = requested && employeeCanAccessPath(access, requested)
                        ? requested
                        : firstAllowedPath(access);
                    if (destination) navigate(destination);
                } else if (employeeDoc.exists()) {
                    setLocalError("This account is not an active admin.");
                }
            } catch (error) {
                console.error('Error checking employee status:', error);
                setLocalError("Unable to verify employee access. Please try again.");
            }
        };

        checkEmployeeAndNavigate();
    }, [currentUser, authState.loading, navigate, location, logout]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLocalError(null);

        if (!email || !password) {
            setLocalError("Please fill in all fields");
            return;
        }

        try {
            await login(email, password);
        } catch (error) {
            console.log(
                `[LOGIN] ${new Date().toISOString()} - Login function threw error:`,
                error
            );
        }
    };
  
    const handleGoogleLogin = async () => {
        setLocalError(null);
        try {
            await loginWithGoogle();
        } catch (error) {
            console.log(`[LOGIN] ${new Date().toISOString()} - Google login error:`, error);
        }
    };

    return (
        <div className="bg-white">
            <div className="w-full h-full">
                <div className="grid xl:grid-cols-2 w-full h-full min-h-screen">
                    <div className="flex flex-col gap-10 items-center justify-center w-full relative p-6">
                        <Link
                            to="/"
                            className="text-2xl absolute top-6 left-6 font-black font-grotesk hover:opacity-80 transition-opacity"
                        >
                            PEAK<span className="font-light text-gray-500">^</span>
                        </Link>
                        <div className="flex flex-col gap-10 items-center justify-center w-full max-w-xl">
                            <h1 className="text-2xl font-black font-grotesk text-center text-gray-900">Sign In</h1>

                            {/* Offline status */}
                            {isOffline && (
                                <div className="p-3 mb-4 text-sm bg-yellow-50 text-yellow-500 border border-yellow-200 rounded-lg">
                                    <span>
                                        You are currently offline.
                                        {hasCachedAuth
                                            ? " Your previous session should be available."
                                            : " You need an internet connection to sign in."}
                                    </span>
                                </div>
                            )}

                            {/* Show cached auth info */}
                            {isOffline && hasCachedAuth && (
                                <div className="p-3 mb-4 text-sm bg-blue-50 text-blue-500 border border-blue-200 rounded-lg">
                                    <span>
                                    You have cached authentication. Please go back or refresh the page
                                    to access your account.
                                    </span>
                                </div>
                            )}

                            {(authState.error || localError) && (
                                <div className="p-3 mb-4 text-sm bg-red-50 text-red-500 border border-red-200 rounded-lg">
                                    <span>{localError || authState.error}</span>
                                </div>
                            )}
                            {/*}
                            <form onSubmit={handleSubmit} className="space-y-4 w-full">
                                <div className="form-group w-full">
                                    <div className="new-form-group w-full">
                                        <input
                                            className="form-element"
                                            id="email"
                                            name="email"
                                            type="email"
                                            autoComplete="email"
                                            autoFocus
                                            required
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            disabled={authState.loading || isOffline}
                                            placeholder="Enter your email"
                                        />
                                        <label htmlFor="email">Email</label>
                                    </div>
                                </div>
                                <div className="form-group w-full">
                                    <div className="new-form-group w-full">
                                        <input
                                            className="form-element"
                                            id="password"
                                            name="password"
                                            type="password"
                                            autoComplete="current-password"
                                            autoFocus
                                            required
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            disabled={authState.loading || isOffline}
                                            placeholder="Enter your password"
                                        />
                                        <label htmlFor="password">Password</label>
                                    </div>
                                </div>
                                <div className="flex justify-end">
                                    <a
                                    href="https://peak.app/forgot-password?return_to=peak.admin"
                                    className="text-gray-900 hover:text-gray-700 text-sm"
                                    >
                                    Forgot password?
                                    </a>
                                </div>
                                <button
                                    type="submit"
                                    className="btn !border-none w-full !bg-gray-900 !text-white !font-grotesk hover:!bg-gray-800"
                                    disabled={authState.loading || isOffline}
                                >
                                    {authState.loading ? (
                                        <div className="flex items-center justify-center">
                                            <svg className="mr-3 -ml-1 size-5 animate-spin text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                        </div>
                                    ) : (
                                        "Sign In"
                                    )}
                                </button>
                                </form>
                                */}
                            <div className="flex flex-col gap-4 text-start text-sm">
                                <p className="font-medium">This website (admin.peak.app) is by invitation only.</p>
                                <ul className="text-xs list-disc list-inside space-y-1">
                                    <li>Unauthorized access, use, or distribution of this system or its contents is strictly prohibited.</li>
                                    <li>If you are not authorized to access this system, please exit immediately.</li>
                                </ul>
                                <div className="text-xs text-gray-600">
                                    <p>All access attempts are logged and monitored. By continuing, you acknowledge that you are an authorized user and agree to comply with all applicable security policies.</p>
                                </div>
                            </div>
                            <button
                                type="button"
                                className="btn bg-transparent shadow-none w-full"
                                onClick={handleGoogleLogin}
                                disabled={authState.loading || isOffline}
                            >
                                <img src={googleIcon} alt="" className="size-5"/>
                                Continue with Google
                            </button>
                        </div>
                    </div>
                    <div className="hidden xl:flex w-full h-full bg-gray-200">
                        <img src={peakhero} alt="Peak Hero" className="w-full h-full object-cover" />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LoginPage;
